import "backbone-associations";
import "underscore";
import { AppSettings } from "../../app-settings";
import { prop } from "../../decorators/property";
import { autoinject } from "aurelia-dependency-injection";
import { backboneModelProperties } from "../../decorators/backbone-properties";
import { AppEvents } from "../../app-events";
import { BrowsePanelModel } from "./browse-panel";
import { PanelTypeOption } from "./panel-type-option";

@autoinject
@backboneModelProperties({
    urlRoot: AppSettings.SearchApiUrlPrefix + "search",
    relations: [{
        type: Backbone.Many,
        key: "Panels",
        relatedModel: Backbone.AssociatedModel
    }],
    defaults: {
        Panels: []
    }
})
export class BrowseModel extends Backbone.AssociatedModel {

    @prop() public Loading: boolean;
    @prop() public Panels: Backbone.Collection<BrowsePanelModel>;
    @prop() public PanelsShiftValue: number;
    @prop() public MaxPanels: number;
    @prop() public PanelWidth: number;
    @prop() public DocumentCount: {count: number, countString: string};
    @prop() public FullTextOnly = false;
    @prop() public SubscriptionOnly = false;

    constructor(options?) {
        super(options);
        this.PanelWidth = 310; // Panel width plus margin
        this.setupFullTextOnlyListener();
    }

    public setupPanels(urlSegments?: string[]) {
        const updatedPanelCount = urlSegments.length;
        this.Panels.remove(this.Panels.rest(updatedPanelCount));
        let i = 0;
        for (const urlSegment of urlSegments) {
             const type = this.getTypeFromUrlSegment(urlSegment);
             const value = this.getValueFromUrlSegment(urlSegment);
             const existingCorrespondingPanel = this.Panels.at(i);
             if (existingCorrespondingPanel) {
                if (existingCorrespondingPanel.SelectedType === type) {
                    if (value) {
                        existingCorrespondingPanel.Value = value;
                    }
                } else {
                    this.Panels.remove(existingCorrespondingPanel);
                    const model = this.createModel(urlSegment);
                    this.Panels.add(model);
                }
             } else {
                const model = this.createModel(urlSegment);
                this.Panels.add(model);
             }
             i++;
        }
        if (this.Panels.last() && (this.Panels.last().Value || this.Panels.last().Value === "0")) {
            const lastPanelType = this.calculateDefaultPanelType(this.Panels.last());
            const url = `${Backbone.history.getFragment()}/${lastPanelType}`;
            Backbone.history.navigate(url, {trigger: true});
        }

        this.setPanelShift();
    }

    public setPanelShift() {
        this.PanelsShiftValue = -(Math.max(this.Panels.size() - this.MaxPanels, 0) * this.PanelWidth);
    }

    private setupFullTextOnlyListener() {
        this.listenTo(this, "change:FullTextOnly", () => {
            this.Panels.each((p) => p.FullTextOnly = this.FullTextOnly);
        });
        this.listenTo(this, "change:SubscriptionOnly", () => {
            this.Panels.each((p) => p.SubscriptionOnly = this.SubscriptionOnly);
        });
    }

    private setupTypeChangeListener(model: BrowsePanelModel) {
        this.listenTo(model.PreRequisites, "change:SelectedType", () => {
            const idx = this.Panels.indexOf(model);
            if (idx === -1) {
                return;
            }
            this.Panels.remove(this.Panels.rest(idx));
        });
    }

    private createModel(urlSegment?: string): BrowsePanelModel {
        const model = new BrowsePanelModel();
        model.FullTextOnly = this.FullTextOnly;
        model.SubscriptionOnly = this.SubscriptionOnly;
        this.Panels.each((panel) => {
            model.PreRequisites.add(panel);
            this.setupTypeChangeListener(model);
        });
        this.buildModelFromUrlSegment(urlSegment, model);
        return model;
    }

    private calculateDefaultPanelType(model: BrowsePanelModel): string  {
        let materialValue;
        if (model.SelectedType === "material") {
            materialValue = model.Value;
        } else {
            materialValue = model.PreRequisites.findWhere((p) => p.SelectedType === "material").Value;
        }
        switch (materialValue) {
            case "case":
                return this.calculateDefaultPanelTypeForCases(model);
            case "statute":
            case "si":
                return this.calculateDefaultPanelTypeForLegislation(model);
            case "eu":
                return this.calculateDefaultPanelTypeForEu(model);
        }
    }

    private calculateDefaultPanelTypeForEu(model: BrowsePanelModel) {
        const preceding = model.SelectedType;
        const yearFieldTaken = this.Panels.any((m) => m.SelectedType === "year");
        const sectorFieldTaken = this.Panels.any((m) => m.SelectedType === "sector");
        const eurovocFieldTaken = (this.Panels.where((m) => m.SelectedType === "eurovoc").length >= 4);
        let sectorValue = "";
        if (sectorFieldTaken) {
            sectorValue = this.Panels.findWhere((p) => p.SelectedType === "sector").Value;
        }
        switch (preceding) {
            case "material":
                return "sector";
            case "sector":
                return sectorValue !== "C" ? "descriptor" : "year";
            case "descriptor":
                return !yearFieldTaken ? "year" : (!eurovocFieldTaken ? "eurovoc" : "finish");
            default:
                return (!eurovocFieldTaken ? "eurovoc" : "finish");
        }
    }

    private calculateDefaultPanelTypeForLegislation(model: BrowsePanelModel) {
        const preceding = model.SelectedType;
        const yearFieldTaken = this.Panels.any((m) => m.SelectedType === "year");
        const jurisdictionFieldTaken = this.Panels.any((m) => m.SelectedType === "jurisdiction");
        const jurisdictionFieldCount = this.Panels.where((m) => m.SelectedType === "jurisdiction").length;

        let subJurisdiction;
        if (jurisdictionFieldCount === 1) {
            const jurisdictionField = this.Panels.findWhere((m) => m.SelectedType === "jurisdiction");
            subJurisdiction = jurisdictionField.Value.endsWith("A");
        }
        switch (preceding) {
            case "material":
                return "jurisdiction";
            case "jurisdiction":
                return subJurisdiction ? "jurisdiction" : (yearFieldTaken ? "finish" : "year");
            case "year":
                return !jurisdictionFieldTaken || subJurisdiction ? "jurisdiction" : "finish";
        }
    }

    private calculateDefaultPanelTypeForCases(model: BrowsePanelModel) {
        const preceding = model.SelectedType;
        const yearFieldTaken = this.Panels.any((m) => m.SelectedType === "year");
        const reportYearTaken = this.Panels.any((m) => m.SelectedType === "reportyear");
        const categoryFieldCount = this.Panels.where((m) => m.SelectedType === "category").length;

        const jurisdictionFieldCount = this.Panels.where((m) => m.SelectedType === "jurisdiction").length;
        const categoryFieldTaken = (this.Panels.where((m) => m.SelectedType === "category").length >= 3);
        let subJurisdiction;
        if (jurisdictionFieldCount === 1) {
            const jurisdictionField = this.Panels.findWhere((m) => m.SelectedType === "jurisdiction");
            subJurisdiction = jurisdictionField.Value.endsWith("A");
        }

        switch (preceding) {
            case "material":
                return "jurisdiction";
            case "jurisdiction":
                return subJurisdiction ? "jurisdiction" : "court";
            case "court":
                return !yearFieldTaken ? "year" : (!categoryFieldTaken ? "category" : "finish");
            case "reports":
                return !reportYearTaken ? "reportyear" : (!categoryFieldTaken ? "category" : "finish");
            default:
                return !categoryFieldTaken ? "category" : (!yearFieldTaken ? "year" : "finish");
        }
    }

    private  getTypeFromUrlSegment(urlSegment: string): string {
        const parts = urlSegment.split("_");
        return parts[0];
    }

    private getValueFromUrlSegment(urlSegment: string): string {
        if (urlSegment.indexOf("_") === -1) {
            return null;
        }
        const parts = urlSegment.split("_");
        return parts[1];
    }

    private buildModelFromUrlSegment(urlSegment: string, model: BrowsePanelModel): BrowsePanelModel {
        model.id = urlSegment;
        model.SelectedType = this.getTypeFromUrlSegment(urlSegment);
        model.SelectedTypeLabel = model.getLabel(model.SelectedType);
        const value = this.getValueFromUrlSegment(urlSegment);
        if (value) {
            model.Value = value;
        }
        return model;
    }

}



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./src/browse/models/browse.ts